import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import useGlobalSettings from '../../../GlobalSettings/hooks/useGlobalSettings';
import { ConfirmDialog, Field, SaveBar, SectionCard, TextInput } from '../../components/forms';
import { TCard, TEmpty, TSkeleton } from '../../components/table';
import { useToast } from '../../components/roster';
import './admin-ops.css';

/*
 * System gallery base URLs: the fallback for every org without its own
 * domain, and the link in gallery-user welcome and rollup messages
 * (production uses the base URL, other environments the alpha URL).
 * The API returns snake_case and accepts camelCase.
 */

const URL_RE = /^https?:\/\/.+/;

const when = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? null
        : d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export default function AdminGlobalSettingsV3() {
    const { getGlobalSettings, updateGlobalSettings } = useGlobalSettings();
    const toast = useToast();

    const [state, setState] = useState<'loading' | 'ok' | 'failed'>('loading');
    const [baseUrl, setBaseUrl] = useState('');
    const [alphaUrl, setAlphaUrl] = useState('');
    const [saved, setSaved] = useState({ baseUrl: '', alphaUrl: '' });
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [tried, setTried] = useState(false);

    const load = useCallback(() => {
        let live = true;
        setState('loading');
        getGlobalSettings().then((s) => {
            if (!live) return;
            if (!s) {
                setState('failed');
                return;
            }
            const base = s.system_gallery_base_url ?? '';
            const alpha = s.system_gallery_alpha_url ?? '';
            setBaseUrl(base);
            setAlphaUrl(alpha);
            setSaved({ baseUrl: base, alphaUrl: alpha });
            setUpdatedAt(s.updated_at ?? null);
            setState('ok');
        });
        return () => {
            live = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(load, [load]);

    const base = baseUrl.trim();
    const alpha = alphaUrl.trim();
    const dirty = base !== saved.baseUrl || alpha !== saved.alphaUrl;
    const baseOk = URL_RE.test(base);
    const alphaOk = URL_RE.test(alpha);

    const askSave = () => {
        if (saving) return;
        setTried(true);
        if (!baseOk || !alphaOk) return;
        setConfirming(true);
    };

    const save = async () => {
        if (saving || !baseOk || !alphaOk) return;
        setSaving(true);
        setSaveError('');
        const updated = await updateGlobalSettings({
            systemGalleryBaseUrl: base,
            systemGalleryAlphaUrl: alpha,
        });
        setSaving(false);
        if (updated) {
            setBaseUrl(base);
            setAlphaUrl(alpha);
            setSaved({ baseUrl: base, alphaUrl: alpha });
            setUpdatedAt(updated.updated_at ?? null);
            setTried(false);
            toast.show('Gallery URLs saved.');
        } else {
            setSaveError('Couldn’t save. Nothing changed; try again.');
        }
    };

    const discard = () => {
        setBaseUrl(saved.baseUrl);
        setAlphaUrl(saved.alphaUrl);
        setTried(false);
        setSaveError('');
    };

    const showErrors = tried || dirty;
    const updatedLabel = when(updatedAt);

    return (
        <AreaShell area="admin" tab="global-settings" sub="Platform-wide settings that apply to every organization">
            <div className="ao-page">
                {state === 'loading' && (
                    <TCard>
                        <TSkeleton rows={2} />
                    </TCard>
                )}
                {state === 'failed' && (
                    <TCard>
                        <TEmpty message="Couldn’t load global settings." actionLabel="Retry" onAction={load} />
                    </TCard>
                )}
                {state === 'ok' && (
                    <SectionCard title="Gallery base URLs" note={updatedLabel ? `Last updated ${updatedLabel}` : undefined}>
                        <p className="ao-note warn" style={{ marginBottom: 14 }}>
                            Every org without its own domain uses these for gallery links, and gallery-user welcome and
                            rollup messages link here. Changes apply immediately.
                        </p>
                        <div className="stack" style={{ gap: 14 }}>
                            <Field
                                label="Production gallery URL"
                                help="Used in production. Full URL, e.g. https://smlbth.io"
                                error={showErrors && !baseOk ? 'Enter a full URL starting with https:// or http://' : undefined}
                            >
                                <TextInput
                                    type="url"
                                    inputMode="url"
                                    className="input ao-mono"
                                    placeholder="https://smlbth.io"
                                    value={baseUrl}
                                    disabled={saving}
                                    aria-invalid={showErrors && !baseOk}
                                    onChange={(e) => setBaseUrl(e.target.value)}
                                />
                            </Field>
                            <Field
                                label="Alpha gallery URL"
                                help="Used by non-production environments."
                                error={showErrors && !alphaOk ? 'Enter a full URL starting with https:// or http://' : undefined}
                            >
                                <TextInput
                                    type="url"
                                    inputMode="url"
                                    className="input ao-mono"
                                    placeholder="https://gallery.alpha.smilebooth.com"
                                    value={alphaUrl}
                                    disabled={saving}
                                    aria-invalid={showErrors && !alphaOk}
                                    onChange={(e) => setAlphaUrl(e.target.value)}
                                />
                            </Field>
                        </div>
                        {saveError && (
                            <p className="ao-note err" role="alert" style={{ marginTop: 14 }}>
                                {saveError}
                            </p>
                        )}
                    </SectionCard>
                )}
            </div>

            <SaveBar
                dirty={state === 'ok' && dirty ? ['Gallery base URLs'] : []}
                saving={saving}
                onSave={askSave}
                onDiscard={discard}
            />
            <ConfirmDialog
                open={confirming}
                title="Change gallery URLs for every org?"
                body={`Every organization without its own domain uses these URLs right away for gallery links and new welcome and rollup messages; links already sent keep the old address. Production: ${base} · Alpha: ${alpha}`}
                actionLabel="Save for every org"
                onConfirm={save}
                onClose={() => setConfirming(false)}
            />
            {toast.node}
        </AreaShell>
    );
}
